import { Fragment, useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { nf, reloadNorms } from "../hooks/useNorms";
import NormSheet, { normValue } from "../components/NormSheet";
import { fmtDT } from "../utils/period";
import { toStation } from "../utils/date";
import type { NormId, NormItem, NormSaveOut, NormsList, Norms } from "../types";

type T = (k: string, v?: Record<string, string | number>) => string;

const GROUPS: [string, NormId[]][] = [
  ["norms.g_voltage", ["v_band", "v_dip"]],
  ["norms.g_trips", ["trip_s"]],
  ["norms.g_freq", ["f_band"]],
  ["norms.g_spikes", ["sharp_kv", "prolonged_s"]],
];
// Where each norm shows up (labels of the sections that read it).
const WHERE: Record<NormId, string[]> = {
  v_band: ["tabs.telemetry", "telemetry.src_pa", "norms.w_band", "events.sub_spikes", "events.sub_incidents", "norms.w_pdf"],
  v_dip: ["norms.w_feed", "tabs.home"],
  trip_s: ["norms.w_feed", "tabs.home"],
  f_band: ["norms.w_pdf"],
  sharp_kv: ["events.sub_spikes"],
  prolonged_s: ["events.sub_spikes"],
};
const LABEL = { margin: "0 2px 6px", fontSize: 12, color: "var(--hint)", textTransform: "uppercase" as const, letterSpacing: ".04em" };
const PILL = { padding: "1px 7px", border: "1px solid var(--border)", borderRadius: 999, fontSize: 11, lineHeight: "16px", color: "var(--hint)", whiteSpace: "nowrap" as const };

/** What a norm gives on the data now, one line under its name. */
function statsLine(it: NormItem, t: T, lang: string): string {
  const s = it.stats;
  const u = (n: number) => s?.units?.find(x => x.unit === n);
  const pct = (x?: number | null) => (x == null ? "—" : nf(Math.round(x), lang));
  switch (it.id) {
    case "v_band": return t("norms.v_band_sub", { g1: pct(u(1)?.pct), g2: pct(u(2)?.pct) });
    case "v_dip": return t("norms.v_dip_sub", { pct: nf(((it.values.v_dip ?? 0) / 20) * 100, lang), n: s?.total ?? "—" });
    case "trip_s": return t("norms.trip_s_sub", { trips: s?.trips ?? "—", stops: s?.stops ?? "—" });
    case "f_band": return t("norms.f_band_sub", { g1: u(1)?.out ?? "—", g2: u(2)?.out ?? "—" });
    default: return t(`norms.${it.id}_sub`);
  }
}

// «Управление → Нормы» (admin): every threshold the app judges the telemetry by, what it
// gives on the data now, a sheet to change one (live preview on all G1/G2 data), history.
export default function NormsPanel() {
  const { t, lang } = useT();
  const [d, setD] = useState<NormsList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<NormItem | null>(null);
  const [done, setDone] = useState<(NormSaveOut & { id: NormId }) | null>(null);
  const [reverting, setReverting] = useState(false);
  const load = useCallback(() => {
    api.get<NormsList>("/api/norms").then(setD).catch((e: ApiError) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  const saved = (r: NormSaveOut & { id: NormId }) => {
    setOpen(null);
    setDone(r.unchanged ? null : r);
    load();
    reloadNorms();  // labels elsewhere («Коридор 19–21», «ниже 18 кВ») follow at once
    tg()?.HapticFeedback?.notificationOccurred("success");
  };
  const revert = async (id: NormId, values: Partial<Norms>) => {
    setReverting(true); setErr(null);
    try {
      saved({ ...(await api.put<NormSaveOut>(`/api/norms/${id}`, { values })), id });
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setReverting(false);
    }
  };
  const current = (id: NormId) => d?.norms.find(n => n.id === id);
  const stamp = (iso: string) => fmtDT(toStation(iso)).slice(0, 16);
  const doneLine = (r: NormSaveOut & { id: NormId }) =>
    r.id === "v_dip" && r.stats ? t("norms.done_dips", { n: r.stats.total ?? "—" })
      : r.id === "trip_s" && r.stats ? t("norms.done_trips", { trips: r.stats.trips ?? "—", stops: r.stats.stops ?? "—" }) : "";

  return (
    <div>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--hint)" }}>{t("norms.intro")}</p>
      {err && <div className="alert error" style={{ marginBottom: 12 }}>{err}</div>}
      {done && (
        <div role="status" style={{ background: "rgba(52,199,89,0.14)", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>✓ {t(done.rebuilt ? "norms.saved_rebuilt" : "norms.saved")}</div>
          <div style={{ fontSize: 13, margin: "4px 0 8px" }}>
            {t("norms." + done.id)}: {normValue(done.id, done.old, t, lang)} → {normValue(done.id, done.new, t, lang)}. {doneLine(done)}
          </div>
          <button className="btn secondary" style={{ width: "auto", minHeight: 36, padding: "6px 14px", fontSize: 14, background: "var(--bg)" }}
            disabled={reverting} onClick={() => revert(done.id, done.old)}>
            {reverting ? <span className="spinner" /> : t("norms.undo")}
          </button>
        </div>
      )}
      {!d && !err && <div className="empty-state"><span className="spinner" /></div>}

      {d && GROUPS.map(([g, ids]) => (
        <Fragment key={g}>
          <div style={LABEL}>{t(g)}</div>
          <div className="card" style={{ padding: "2px 14px" }}>
            {ids.map((id, i) => {
              const it = current(id);
              if (!it) return null;
              const changed = it.keys.some(k => it.values[k] !== it.defaults[k]);
              return (
                <button key={id} onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); setOpen(it); }}
                  style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", columnGap: 10, rowGap: 4, width: "100%", padding: "12px 0",
                           border: "none", borderTop: i ? "1px solid var(--border)" : "none", background: "transparent", color: "var(--text)",
                           font: "inherit", textAlign: "start", cursor: "pointer" }}>
                  <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, fontWeight: 600 }}>
                    <span>{t("norms." + id)}</span>
                    {changed && <span style={{ ...PILL, border: "none", fontWeight: 600, background: "var(--yellow)", color: "var(--yellow-text)" }}>{t("norms.changed")}</span>}
                  </span>
                  <span style={{ fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                    {normValue(id, it.values, t, lang)} <span style={{ fontWeight: 400, color: "var(--hint)" }}>›</span>
                  </span>
                  <span style={{ gridColumn: "1 / -1", fontSize: 13, color: "var(--hint)" }}>
                    {statsLine(it, t, lang)}{changed ? " · " + t("norms.default_is", { v: normValue(id, it.defaults, t, lang) }) : ""}
                  </span>
                  <span style={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {WHERE[id].map(w => <span key={w} style={PILL}>{t(w)}</span>)}
                  </span>
                </button>
              );
            })}
          </div>
        </Fragment>
      ))}

      {d && (
        <>
          <div style={LABEL}>{t("norms.g_history")}</div>
          {d.history.length === 0 ? (
            <div className="card" style={{ fontSize: 13, color: "var(--hint)" }}>{t("norms.history_empty")}</div>
          ) : (
            <div className="card" style={{ padding: "2px 14px" }}>
              {d.history.slice(0, 10).map((h, i) => {
                const now = current(h.norm);
                const latest = !!now && now.keys.every(k => h.new[k] === now.values[k]) && d.history.find(x => x.norm === h.norm) === h;
                return (
                  <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: i ? "1px solid var(--border)" : "none" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14 }}>{t("norms." + h.norm)}: {normValue(h.norm, h.old, t, lang)} → {normValue(h.norm, h.new, t, lang)}</div>
                      <div style={{ fontSize: 12, color: "var(--hint)" }}>{stamp(h.changed_at)} · {h.changed_by_name || "—"}</div>
                    </div>
                    {latest && (
                      <button className="btn secondary" style={{ width: "auto", minHeight: 34, padding: "4px 12px", fontSize: 13, background: "var(--bg)" }}
                        disabled={reverting} onClick={() => revert(h.norm, h.old)}>{t("norms.revert")}</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p style={{ margin: "12px 0 0", fontSize: 13, color: "var(--hint)" }}>{t("norms.footer")}</p>
        </>
      )}
      {open && <NormSheet item={open} onClose={() => setOpen(null)} onSaved={saved} />}
    </div>
  );
}
